import { platforms_ebay_reviews } from "@/constants";
import { authedFetch, isTokenInvalid } from "@/lib/api-client";
import type {
  ReviewConfig,
  ReviewExport,
  ReviewList,
  ReviewListFilters,
  ReviewVisibility,
} from "@/lib/types";
import { queryClient } from "@/main";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import type { AuthFetchParams } from "./global";

export const REVIEWS_PAGE_SIZE = 20;
// how often the page asks how an import is going
const IMPORT_POLL_MS = 3000;

// the session can't be refreshed, e.g. the JWT secret was rotated
class TokenInvalidError extends Error {}

// the API's message for requests it rejected
export class ReviewRequestError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function reviewRequest<T>(
  path: string,
  params: AuthFetchParams,
  method: "GET" | "POST" = "GET",
  body?: unknown,
) {
  const resp = await authedFetch(`${platforms_ebay_reviews}${path}`, {
    method,
    token: params.token,
    orgId: params.orgId,
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });

  if (await isTokenInvalid(resp)) throw new TokenInvalidError();

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  if (!resp.ok) {
    const data = (await resp.json().catch(() => null)) as {
      msg?: unknown;
    } | null;
    throw new ReviewRequestError(
      resp.status,
      typeof data?.msg === "string" ? data.msg : resp.statusText,
    );
  }
  return (await resp.json()) as T;
}

// an import runs in the background, its config is asked for until it's done
export const reviewConfigQueryOpts = (pcId: string, params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["ebayReviews", pcId, "config"],
    queryFn: () =>
      reviewRequest<ReviewConfig>(`/${encodeURIComponent(pcId)}`, params),
    refetchInterval: (query) =>
      query.state.data?.import_status === "RUNNING" ? IMPORT_POLL_MS : false,
  });

export const reviewListQueryOpts = (
  pcId: string,
  { status, rating, page }: ReviewListFilters,
  params: AuthFetchParams,
) => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(REVIEWS_PAGE_SIZE),
  });
  if (status) query.set("status", status);
  if (rating) query.set("rating", String(rating));

  return queryOptions({
    queryKey: ["ebayReviews", pcId, "list", status, rating, page],
    queryFn: () =>
      reviewRequest<ReviewList>(
        `/${encodeURIComponent(pcId)}/list?${query}`,
        params,
      ),
    // the last page stays up while the next one loads
    placeholderData: (previous) => previous,
  });
};

function useReviewParams() {
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  return { token: getToken() as string, setToken, orgId: orgId as string };
}

// the config the API answers a change with is shown straight away, the
// reviews are listed again
function cacheConfig(config: ReviewConfig) {
  queryClient.setQueryData(
    ["ebayReviews", config.platform_connection_id, "config"],
    config,
  );
  void queryClient.invalidateQueries({
    queryKey: ["ebayReviews", config.platform_connection_id, "list"],
  });
}

function useTokenCheck() {
  const { handleTokenInvalid } = useAuth();
  return async (error: unknown) => {
    if (error instanceof TokenInvalidError) {
      await handleTokenInvalid();
      return true;
    }
    return false;
  };
}

export const useReviewImportMutation = () => {
  const params = useReviewParams();
  const tokenInvalid = useTokenCheck();

  return useMutation({
    mutationFn: (pcId: string) =>
      reviewRequest<ReviewConfig>("/import", params, "POST", { pc_id: pcId }),
    onSuccess: (config) => {
      toast.success(i18n.t("reviews.toast.importStarted"));
      cacheConfig(config);
    },
    onError: async (error) => {
      if (await tokenInvalid(error)) return;
      toast.error(
        i18n.t(
          error instanceof ReviewRequestError && error.status === 409
            ? "reviews.toast.importRunning"
            : "reviews.toast.importFailed",
        ),
      );
    },
  });
};

export const useReviewSettingsMutation = () => {
  const params = useReviewParams();
  const tokenInvalid = useTokenCheck();

  return useMutation({
    mutationFn: (body: {
      pc_id: string;
      shopify_pc_id: string;
      ratings: number[];
      min_length: number;
      exclude_keywords: string[];
      skip_automatic: boolean;
    }) => reviewRequest<ReviewConfig>("/settings", params, "POST", body),
    onSuccess: (config) => {
      toast.success(
        i18n.t(
          config.shopify_platform_connection_id
            ? "reviews.toast.settingsSavedStore"
            : "reviews.toast.settingsSaved",
        ),
      );
      cacheConfig(config);
    },
    onError: async (error) => {
      if (await tokenInvalid(error)) return;
      toast.error(i18n.t("reviews.toast.settingsFailed"));
    },
  });
};

export const useReviewVisibilityMutation = () => {
  const params = useReviewParams();
  const tokenInvalid = useTokenCheck();

  return useMutation({
    mutationFn: (body: {
      pc_id: string;
      review_ids: string[];
      visibility: ReviewVisibility;
    }) => reviewRequest<ReviewConfig>("/visibility", params, "POST", body),
    onSuccess: (config, { visibility }) => {
      toast.success(
        i18n.t(
          visibility === "HIDDEN"
            ? "reviews.toast.reviewHidden"
            : visibility === "SHOWN"
              ? "reviews.toast.reviewShown"
              : "reviews.toast.reviewFiltered",
        ),
      );
      cacheConfig(config);
    },
    onError: async (error) => {
      if (await tokenInvalid(error)) return;
      toast.error(i18n.t("reviews.toast.reviewFailed"));
    },
  });
};

function download({ filename, csv }: ReviewExport) {
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const useReviewExportMutation = () => {
  const params = useReviewParams();
  const tokenInvalid = useTokenCheck();

  return useMutation({
    mutationFn: (pcId: string) =>
      reviewRequest<ReviewExport>(
        `/${encodeURIComponent(pcId)}/export`,
        params,
      ),
    onSuccess: (exported) => {
      if (exported.count === 0) {
        toast.error(i18n.t("reviews.toast.nothingToDownload"));
        return;
      }
      download(exported);
      toast.success(
        i18n.t("reviews.toast.downloaded", { count: exported.count }),
      );
    },
    onError: async (error) => {
      if (await tokenInvalid(error)) return;
      toast.error(i18n.t("reviews.toast.downloadFailed"));
    },
  });
};
